"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { disableTotp, getCurrentUser } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import type { UserDto } from "@/types/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function TwoFactorSettings({ customer = false, onChange }: { customer?: boolean; onChange?: (user: UserDto) => void }) {
  const [user, setUser] = useState<UserDto | null>(null);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => { getCurrentUser().then(value => { setUser(value); if (!value) setError("Could not load security settings."); }); }, []);
  return <section aria-label="Two-factor authentication" className="mt-8 max-w-xl space-y-4 border-t pt-6">
    <h2 className="text-xl font-semibold">Two-factor authentication</h2>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {!user && !error && <p role="status">Loading security settings...</p>}
    {user && <>
      <p>{user.totpEnabled ? "Enabled" : "Off"}. {user.role === "super_admin" ? "Required for platform super admins." : "Optional for your account."}</p>
      {!user.totpEnabled && <Link className="inline-flex min-h-11 items-center underline" href={customer ? "/auth/totp-setup?returnTo=account" : "/auth/totp-setup"}>Set up an authenticator</Link>}
      {user.totpEnabled && user.role !== "super_admin" && <form className="space-y-4" onSubmit={async event => {
        event.preventDefault(); setBusy(true); setError(""); setMessage("");
        try { const updated = await disableTotp(password, code); setUser(updated); setPassword(""); setCode(""); setMessage("Two-factor authentication is off."); onChange?.(updated); }
        catch (e) { setError(formatApiError(e)); }
        finally { setBusy(false); }
      }}>
        {user.hasPassword && <div><Label htmlFor="disable-2fa-password">Current password to turn off 2FA</Label><Input id="disable-2fa-password" type="password" autoComplete="current-password" required maxLength={100} value={password} onChange={e => setPassword(e.target.value)} /></div>}
        <div><Label htmlFor="disable-2fa-code">Current authenticator code</Label><Input id="disable-2fa-code" inputMode="numeric" autoComplete="one-time-code" required pattern="[0-9]{6}" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} /></div>
        <Button variant="outline" type="submit" disabled={busy}>{busy ? "Saving..." : "Turn off two-factor authentication"}</Button>
      </form>}
    </>}
  </section>;
}
